// QuestionJsonEditor — editor JSON por questão, com syntax highlight
// VS Code-style (CodeMirror 6 + tema One Dark / GitHub Light), JSON linter
// inline e botões explícitos Aplicar/Reverter.
//
// O draft só vai pro estado pai quando o admin clica "Aplicar à questão" —
// erros de parse durante digitação NÃO derrubam o último estado bom.
//
// Reage ao toggle de tema (dark/light) do shell via MutationObserver no
// classList do <html>. [[feedback-no-black-in-dark-mode]]

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { EditorState } from "@codemirror/state";
import {
  EditorView,
  lineNumbers,
  highlightActiveLine,
  keymap
} from "@codemirror/view";
import { defaultKeymap, history, historyKeymap } from "@codemirror/commands";
import {
  bracketMatching,
  indentOnInput,
  syntaxHighlighting,
  defaultHighlightStyle
} from "@codemirror/language";
import { closeBrackets, closeBracketsKeymap } from "@codemirror/autocomplete";
import { json, jsonParseLinter } from "@codemirror/lang-json";
import { linter, lintGutter, lintKeymap } from "@codemirror/lint";
import { oneDark } from "@codemirror/theme-one-dark";
import {
  QuestionPreview,
  type PreviewQuestion
} from "@app/ui/question-preview";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  RotateCcw
} from "lucide-react";

function useDarkMode(): boolean {
  const [dark, setDark] = useState<boolean>(() => {
    if (typeof document === "undefined") return false;
    return document.documentElement.classList.contains("dark");
  });
  useEffect(() => {
    if (typeof document === "undefined") return;
    const obs = new MutationObserver(() => {
      setDark(document.documentElement.classList.contains("dark"));
    });
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"]
    });
    return () => obs.disconnect();
  }, []);
  return dark;
}

interface QuestionJsonEditorProps {
  value: PreviewQuestion;
  onApply: (next: PreviewQuestion) => void;
  // Opcional: altura mínima do editor (default 240px).
  minHeight?: number | string;
  // Quando true, o painel ocupa o espaço vertical disponível do pai (flex).
  flex?: boolean;
}

export function QuestionJsonEditor({
  value,
  onApply,
  minHeight = 240,
  flex = false
}: QuestionJsonEditorProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<EditorView | null>(null);
  const dark = useDarkMode();

  const initial = useMemo(() => JSON.stringify(value, null, 2), [value]);
  const [draft, setDraft] = useState<string>(initial);
  const draftRef = useRef<string>(initial);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  // Quando a prop `value` muda (admin navega pra outra questão), reseta o
  // draft pro JSON dela. Não destrói/reconstrói o view.
  useEffect(() => {
    setDraft(initial);
    setError(null);
    if (viewRef.current) {
      const cur = viewRef.current.state.doc.toString();
      if (cur !== initial) {
        viewRef.current.dispatch({
          changes: { from: 0, to: cur.length, insert: initial }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  // Constrói/reconstrói o editor view quando o tema muda. Mantém o conteúdo
  // atual (draftRef) ao trocar de tema.
  useEffect(() => {
    if (!containerRef.current) return;
    const state = EditorState.create({
      doc: draftRef.current,
      extensions: [
        lineNumbers(),
        history(),
        bracketMatching(),
        closeBrackets(),
        indentOnInput(),
        highlightActiveLine(),
        syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
        keymap.of([
          ...defaultKeymap,
          ...historyKeymap,
          ...closeBracketsKeymap,
          ...lintKeymap
        ]),
        json(),
        lintGutter(),
        linter(jsonParseLinter()),
        dark ? oneDark : [],
        EditorView.theme({
          "&": {
            fontSize: "12px",
            height: flex ? "100%" : "auto"
          },
          ".cm-content": { padding: "10px 0" },
          ".cm-scroller": {
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
            lineHeight: "1.55"
          },
          ".cm-gutters": {
            backgroundColor: dark ? "#1e1e1e" : "#f8f8f8",
            border: "none"
          }
        }),
        EditorView.updateListener.of((upd) => {
          if (upd.docChanged) {
            const next = upd.state.doc.toString();
            setDraft(next);
            if (!next.trim()) {
              setError("JSON vazio");
              return;
            }
            try {
              JSON.parse(next);
              setError(null);
            } catch (err) {
              setError(
                err instanceof Error ? err.message : "JSON inválido"
              );
            }
          }
        })
      ]
    });
    const view = new EditorView({
      state,
      parent: containerRef.current
    });
    viewRef.current = view;
    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dark, flex]);

  const dirty = draft !== initial;
  const canApply = dirty && !error;

  function handleApply() {
    try {
      const parsed = JSON.parse(draft) as PreviewQuestion;
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        setError("JSON precisa ser um objeto (não array nem primitivo).");
        return;
      }
      onApply(parsed);
    } catch (err) {
      setError(err instanceof Error ? err.message : "JSON inválido");
    }
  }

  function handleRevert() {
    setDraft(initial);
    setError(null);
    if (viewRef.current) {
      const cur = viewRef.current.state.doc.toString();
      viewRef.current.dispatch({
        changes: { from: 0, to: cur.length, insert: initial }
      });
    }
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(draft);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard pode falhar em http — silencioso */
    }
  }

  return (
    <div class={flex ? "flex h-full flex-col gap-2" : "space-y-2"}>
      <div
        ref={containerRef}
        class={`overflow-hidden rounded-md border ${
          error ? "border-destructive/60" : "border-border"
        } ${flex ? "flex-1 min-h-0" : ""}`}
        style={
          flex
            ? undefined
            : { minHeight: typeof minHeight === "number" ? `${minHeight}px` : minHeight }
        }
      />
      <div class="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={handleApply}
          disabled={!canApply}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
          title="Aplicar este JSON à questão"
        >
          <Check className="size-3.5" /> Aplicar à questão
        </button>
        <button
          type="button"
          onClick={handleRevert}
          disabled={!dirty}
          class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
          title="Voltar ao JSON da questão original"
        >
          <RotateCcw className="size-3.5" /> Reverter
        </button>
        <button
          type="button"
          onClick={handleCopy}
          class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
          title="Copiar JSON desta questão (útil pra pedir ajuda à IA externa)"
        >
          <Copy className="size-3.5" /> {copied ? "Copiado!" : "Copiar"}
        </button>

        <span class="ml-auto text-[11px]">
          {error ? (
            <span class="font-medium text-destructive">{error}</span>
          ) : dirty ? (
            <span class="font-medium text-amber-600 dark:text-amber-400">
              • Há alterações não aplicadas
            </span>
          ) : (
            <span class="text-muted-foreground">Em sincronia com a questão</span>
          )}
        </span>
      </div>
    </div>
  );
}

// =====================
// QuestionJsonByOnePane — wrapper: nav + JSON editor + prévia ao vivo.
// Usado como terceiro modo do UploadBankPage. Edita uma questão por vez
// substituindo no array em onChange. [[project-visual-bank-editor]]
// =====================

interface QuestionJsonByOnePaneProps {
  questoes: PreviewQuestion[];
  index: number;
  onIndexChange: (n: number) => void;
  onChange: (next: PreviewQuestion[]) => void;
}

export function QuestionJsonByOnePane({
  questoes,
  index,
  onIndexChange,
  onChange
}: QuestionJsonByOnePaneProps) {
  const total = questoes.length;
  const safeIdx = total === 0 ? 0 : Math.min(Math.max(0, index), total - 1);
  const q = (questoes[safeIdx] ?? {}) as PreviewQuestion;

  function applyOne(next: PreviewQuestion) {
    const arr = questoes.slice();
    arr[safeIdx] = next;
    onChange(arr);
  }

  if (total === 0) {
    return (
      <div class="rounded-md border border-dashed border-border bg-background/40 p-6 text-center text-sm text-muted-foreground">
        Sem questões para editar.
      </div>
    );
  }

  return (
    <div class="space-y-3">
      {/* Nav */}
      <header class="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card/40 px-3 py-2">
        <button
          type="button"
          onClick={() => onIndexChange(Math.max(0, safeIdx - 1))}
          disabled={safeIdx === 0}
          class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs font-medium hover:border-primary disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronLeft className="size-3" /> Anterior
        </button>
        <div class="inline-flex items-center gap-1 text-xs">
          <span class="text-muted-foreground">Questão</span>
          <input
            type="number"
            min={1}
            max={total}
            value={safeIdx + 1}
            onInput={(e) => {
              const n = Number((e.target as HTMLInputElement).value);
              if (Number.isFinite(n)) {
                onIndexChange(Math.min(Math.max(0, n - 1), total - 1));
              }
            }}
            aria-label="Ir para a questão"
            class="w-14 rounded-md border border-border bg-background px-1.5 py-0.5 text-center text-xs font-bold outline-none focus:border-primary"
          />
          <span class="text-muted-foreground">de {total}</span>
        </div>
        <button
          type="button"
          onClick={() => onIndexChange(Math.min(total - 1, safeIdx + 1))}
          disabled={safeIdx >= total - 1}
          class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs font-medium hover:border-primary disabled:cursor-not-allowed disabled:opacity-40"
        >
          Próxima <ChevronRight className="size-3" />
        </button>
        <span class="ml-auto text-[10px] text-muted-foreground">
          Edite o JSON desta questão e clique <strong>Aplicar</strong>. Útil
          para colar uma versão da questão gerada por IA externa.
        </span>
      </header>

      {/* Editor + Preview lado a lado */}
      <div class="grid gap-4 lg:grid-cols-2">
        <QuestionJsonEditor value={q} onApply={applyOne} minHeight={420} />
        <aside class="lg:sticky lg:top-4 lg:self-start">
          <div class="rounded-md border border-border bg-card/40 p-3">
            <header class="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Como o aluno vê
            </header>
            <QuestionPreview q={q} showGabarito />
          </div>
        </aside>
      </div>
    </div>
  );
}
